import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Tab, Tabs } from "@mui/material";
import DataListPage from "../../../components/DataListPage";
import DataListDateRange from "../../../components/DataListDateRange";
import TruncatedCell from "../../../components/TruncatedCell";
import { instanceToken } from "../../../utils/constant";
import { formatDetailDate } from "../../../utils/detailUtils";
import {
  buildListFilterParams,
  exportPolicyDocumentsToExcel,
  fetchAllDocuments,
  fetchDocuments,
  formatLanguageLabel,
  LANGUAGE_OPTIONS,
  SCOPES,
  isValidScope,
} from "./policyDocumentUtils";

export default function PolicyDocuments() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialScope = searchParams.get("scope");
  const [scope, setScope] = useState(
    isValidScope(initialScope) ? initialScope : SCOPES.general
  );
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [count, setCount] = useState(0);
  const [documents, setDocuments] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [scopeFilter, setScopeFilter] = useState("");
  const [documentTypeFilter, setDocumentTypeFilter] = useState("");
  const [languageFilter, setLanguageFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const filterParams = useMemo(
    () =>
      buildListFilterParams({
        scope,
        scopeFilter,
        documentTypeFilter,
        languageFilter,
        dateFrom,
        dateTo,
      }),
    [scope, scopeFilter, documentTypeFilter, languageFilter, dateFrom, dateTo]
  );

  const loadDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildListFilterParams({
        scope,
        scopeFilter,
        documentTypeFilter,
        languageFilter,
        dateFrom,
        dateTo,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      });

      const data = await fetchDocuments(
        scope,
        instanceToken.token,
        params
      );
      setDocuments(data.documents || []);
      setCount(data.count || 0);
    } catch (error) {
      console.error(error);
      setDocuments([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [scope, page, rowsPerPage, scopeFilter, documentTypeFilter, languageFilter, dateFrom, dateTo]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const handleScopeChange = (_, newScope) => {
    setScope(newScope);
    setPage(0);
    setScopeFilter("");
    setDocumentTypeFilter("");
    setLanguageFilter("");
    setDateFrom("");
    setDateTo("");
    setSearchParams({ scope: newScope });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleDateRangeChange = ({ from, to }) => {
    setDateFrom(from);
    setDateTo(to);
    setPage(0);
  };

  const clearDateRange = () => {
    setDateFrom("");
    setDateTo("");
    setPage(0);
  };

  const exportRows = (rows) => {
    const prefix =
      scope === SCOPES.general
        ? "policy-documents-general"
        : "policy-documents-customer";
    exportPolicyDocumentsToExcel(scope, rows, prefix);
  };

  const handleExportCurrentPage = () => {
    exportRows(documents || []);
  };

  const handleExportAllFiltered = async () => {
    try {
      setExporting(true);
      const data = await fetchAllDocuments(
        scope,
        instanceToken.token,
        filterParams
      );
      exportRows(data.documents || []);
    } catch (error) {
      console.error(error);
    } finally {
      setExporting(false);
    }
  };

  const openDetail = (row) => navigate(`/policy-documents/${scope}/${row.id}`);

  const generalColumns = useMemo(
    () => [
      { id: "id", label: "ID", render: (row) => row.id },
      {
        id: "product_type",
        label: "Product Name",
        render: (row) => <TruncatedCell value={row.product_type} />,
      },
      {
        id: "document_type",
        label: "Document Type",
        render: (row) => <TruncatedCell value={row.document_type} />,
      },
      {
        id: "language",
        label: "Language",
        render: (row) => formatLanguageLabel(row.language),
      },
      {
        id: "file_name",
        label: "File Name",
        render: (row) => <TruncatedCell value={row.file_name} />,
      },
      {
        id: "created_at",
        label: "Created Date",
        render: (row) =>
          formatDetailDate(row.created_at || row.createdAt) || "—",
      },
    ],
    []
  );

  const customerColumns = useMemo(
    () => [
      { id: "id", label: "ID", render: (row) => row.id },
      {
        id: "policy_no",
        label: "Policy No",
        width: "180px",
        render: (row) => row.policy_no || "—",
      },
      {
        id: "document_type",
        label: "Document Type",
        render: (row) => <TruncatedCell value={row.document_type} />,
      },
      {
        id: "language",
        label: "Language",
        render: (row) => formatLanguageLabel(row.language),
      },
      {
        id: "file_name",
        label: "File Name",
        render: (row) => <TruncatedCell value={row.file_name} />,
      },
      {
        id: "created_at",
        label: "Created Date",
        render: (row) =>
          formatDetailDate(row.created_at || row.createdAt) || "—",
      },
    ],
    []
  );

  const scopeLabel = scope === SCOPES.general ? "Product Name" : "Policy No";

  return (
    <DataListPage
      title="Policy Documents"
      rows={documents || []}
      columns={scope === SCOPES.general ? generalColumns : customerColumns}
      count={count}
      page={page}
      rowsPerPage={rowsPerPage}
      onPageChange={handleChangePage}
      onRowsPerPageChange={handleChangeRowsPerPage}
      headerActions={[
        {
          label: "Export to Excel",
          onClick: handleExportCurrentPage,
          disabled: exporting || !documents?.length,
        },
        {
          label: exporting ? "Exporting..." : "Export All (filtered)",
          onClick: handleExportAllFiltered,
          disabled: exporting,
        },
      ]}
      createButton={{
        label: "Upload",
        onClick: () =>
          navigate(`/policy-documents/create?scope=${scope}`),
      }}
      onView={openDetail}
      menuActions={[{ label: "View details", onClick: openDetail }]}
      loading={loading && documents === null}
      toolbarExtra={
        <div className="policy-docs-toolbar">
          <Tabs
            value={scope}
            onChange={handleScopeChange}
            className="policy-docs-tabs"
          >
            <Tab label="General" value={SCOPES.general} />
            <Tab label="Customer" value={SCOPES.customer} />
          </Tabs>
          <div className="policy-docs-filters">
            <input
              type="text"
              className="policy-docs-filter-input"
              placeholder={`Filter by ${scopeLabel}`}
              value={scopeFilter}
              onChange={(e) => {
                setScopeFilter(e.target.value);
                setPage(0);
              }}
            />
            <input
              type="text"
              className="policy-docs-filter-input"
              placeholder="Filter by Document Type"
              value={documentTypeFilter}
              onChange={(e) => {
                setDocumentTypeFilter(e.target.value);
                setPage(0);
              }}
            />
            <select
              className="policy-docs-filter-input"
              value={languageFilter}
              onChange={(e) => {
                setLanguageFilter(e.target.value);
                setPage(0);
              }}
            >
              <option value="">All Languages</option>
              {LANGUAGE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <DataListDateRange
              from={dateFrom}
              to={dateTo}
              onChange={handleDateRangeChange}
              onClear={clearDateRange}
            />
          </div>
        </div>
      }
    />
  );
}
